import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  BookOpen,
  Plus,
  Receipt,
  CheckSquare,
  BarChart3,
  Settings,
  User,
  Camera,
  ScanLine,
  Car,
  Filter,
  CheckCheck,
  Download,
  UserCog,
  Building2,
  Tag,
  ArrowRight,
  Info,
  Lightbulb,
  AlertCircle
} from "lucide-react";

const RoleBadge = ({ role }) => {
  const colors = {
    "Tous": "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
    "Manager": "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300",
    "Comptable": "bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300",
    "Admin": "bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300",
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${colors[role] || colors["Tous"]}`}>
      {role}
    </span>
  );
};

const Tip = ({ children }) => (
  <div className="flex gap-2 mt-3 p-3 bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 rounded-lg text-sm text-emerald-800 dark:text-emerald-300">
    <Lightbulb className="h-4 w-4 shrink-0 mt-0.5" />
    <span>{children}</span>
  </div>
);

const Note = ({ children }) => (
  <div className="flex gap-2 mt-3 p-3 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg text-sm text-blue-800 dark:text-blue-300">
    <Info className="h-4 w-4 shrink-0 mt-0.5" />
    <span>{children}</span>
  </div>
);

const Warning = ({ children }) => (
  <div className="flex gap-2 mt-3 p-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg text-sm text-amber-800 dark:text-amber-300">
    <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
    <span>{children}</span>
  </div>
);

const Step = ({ number, children }) => (
  <div className="flex gap-3 items-start">
    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs font-bold flex items-center justify-center mt-0.5">
      {number}
    </span>
    <span className="text-sm leading-relaxed">{children}</span>
  </div>
);

const SectionHeader = ({ icon: Icon, title, roles = [] }) => (
  <div className="flex items-center gap-3 mb-4">
    <div className="p-2 rounded-lg bg-primary/10">
      <Icon className="h-5 w-5 text-primary" />
    </div>
    <div>
      <h2 className="text-lg font-semibold">{title}</h2>
      {roles.length > 0 && (
        <div className="flex gap-1 mt-0.5">
          {roles.map(r => <RoleBadge key={r} role={r} />)}
        </div>
      )}
    </div>
  </div>
);

// ─── SECTIONS ───────────────────────────────────────────────────────────────

function SectionDepart() {
  return (
    <div className="space-y-4">
      <SectionHeader icon={BookOpen} title="Premiers pas" />
      <p className="text-sm text-muted-foreground">
        NoteFrais vous permet de saisir, suivre et faire valider vos notes de frais professionnelles depuis n'importe quel appareil.
      </p>

      <Accordion type="multiple" className="space-y-2">
        <AccordionItem value="roles" className="border rounded-lg px-4">
          <AccordionTrigger className="text-sm font-medium py-3">Comprendre les rôles utilisateurs</AccordionTrigger>
          <AccordionContent className="pb-3">
            <div className="space-y-3 text-sm">
              <div className="flex items-start gap-3">
                <RoleBadge role="Tous" />
                <div>
                  <p className="font-medium">Collaborateur</p>
                  <p className="text-muted-foreground">Crée et soumet ses propres dépenses. Consulte son historique.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <RoleBadge role="Manager" />
                <div>
                  <p className="font-medium">Manager</p>
                  <p className="text-muted-foreground">En plus du collaborateur : valide ou rejette les dépenses de son équipe, accède aux rapports.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <RoleBadge role="Comptable" />
                <div>
                  <p className="font-medium">Comptable</p>
                  <p className="text-muted-foreground">Valide, rembourse, exporte (CSV, FEC SAGE), gère les catégories et paramètres de l'entreprise.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <RoleBadge role="Admin" />
                <div>
                  <p className="font-medium">Administrateur</p>
                  <p className="text-muted-foreground">Tous les droits : gestion des utilisateurs, rôles, et configuration complète.</p>
                </div>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="workflow" className="border rounded-lg px-4">
          <AccordionTrigger className="text-sm font-medium py-3">Le cycle de vie d'une dépense</AccordionTrigger>
          <AccordionContent className="pb-3">
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <Badge variant="secondary">Brouillon</Badge>
              <ArrowRight className="h-4 w-4 text-muted-foreground" />
              <Badge className="bg-blue-500 text-white">Soumis</Badge>
              <ArrowRight className="h-4 w-4 text-muted-foreground" />
              <Badge className="bg-emerald-500 text-white">Approuvé</Badge>
              <ArrowRight className="h-4 w-4 text-muted-foreground" />
              <Badge className="bg-purple-500 text-white">Remboursé</Badge>
            </div>
            <p className="text-sm text-muted-foreground mt-3">
              À tout moment, un manager peut <Badge variant="destructive" className="text-xs">Rejeter</Badge> une dépense soumise, en indiquant la raison. Le collaborateur peut alors la corriger et la soumettre à nouveau.
            </p>
            <Note>Une dépense remboursée est définitive et ne peut plus être modifiée.</Note>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="pwa" className="border rounded-lg px-4">
          <AccordionTrigger className="text-sm font-medium py-3">Installer l'application sur mobile</AccordionTrigger>
          <AccordionContent className="pb-3 text-sm space-y-2">
            <p>NoteFrais est une Progressive Web App (PWA) : elle s'installe sur votre téléphone comme une vraie application, sans passer par un store.</p>
            <div className="space-y-1">
              <Step number="1">Sur Android (Chrome) : une bannière <strong>"Installer l'application"</strong> apparaît automatiquement en bas de l'écran. Appuyez sur "Installer".</Step>
              <Step number="2">Sur iOS (Safari) : appuyez sur l'icône de partage <strong>⬆</strong> en bas du navigateur, puis <strong>"Sur l'écran d'accueil"</strong>.</Step>
            </div>
            <Tip>Une fois installée, l'application fonctionne en plein écran et est accessible depuis votre bureau.</Tip>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}

function SectionCreerDepense() {
  return (
    <div className="space-y-4">
      <SectionHeader icon={Plus} title="Créer une dépense" />

      <Accordion type="multiple" className="space-y-2">
        <AccordionItem value="saisie" className="border rounded-lg px-4">
          <AccordionTrigger className="text-sm font-medium py-3">Saisie manuelle</AccordionTrigger>
          <AccordionContent className="pb-3 space-y-2">
            <p className="text-sm">Depuis <strong>Mes dépenses</strong>, cliquez sur <strong>"Nouvelle dépense"</strong> (ou le bouton <kbd className="px-1.5 py-0.5 bg-muted rounded text-xs">+</kbd> sur mobile).</p>
            <div className="space-y-1">
              <Step number="1">Saisissez la <strong>description</strong> de la dépense (ex. : "Déjeuner client Paris").</Step>
              <Step number="2">Entrez le <strong>montant</strong> et sélectionnez la <strong>devise</strong>.</Step>
              <Step number="3">Choisissez la <strong>catégorie</strong> (repas, transport, hébergement…).</Step>
              <Step number="4">Indiquez la <strong>date de la dépense</strong> et le <strong>mois comptable</strong> (AAAA-MM).</Step>
              <Step number="5">Ajoutez optionnellement la <strong>TVA</strong>, un <strong>projet</strong> et des <strong>notes</strong>.</Step>
              <Step number="6">Cliquez sur <strong>"Enregistrer"</strong> (brouillon) ou <strong>"Soumettre"</strong> (envoi direct pour validation).</Step>
            </div>
            <Tip>Le mois comptable peut différer de la date réelle de la dépense (ex. : note de frais de décembre traitée en janvier).</Tip>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="justificatif" className="border rounded-lg px-4">
          <AccordionTrigger className="text-sm font-medium py-3 gap-2">
            <span className="flex items-center gap-2"><ScanLine className="h-4 w-4" /> Scanner un justificatif (OCR)</span>
          </AccordionTrigger>
          <AccordionContent className="pb-3 space-y-2">
            <p className="text-sm">L'OCR extrait automatiquement le montant, la date et le fournisseur depuis une photo ou un PDF.</p>
            <div className="space-y-1">
              <Step number="1">Dans le formulaire de nouvelle dépense, faites glisser votre fichier dans la zone d'upload, ou cliquez pour sélectionner un fichier (JPG, PNG, PDF).</Step>
              <Step number="2">L'analyse démarre automatiquement. Les champs montant, date et description se pré-remplissent si l'extraction réussit.</Step>
              <Step number="3">Vérifiez et corrigez les valeurs si nécessaire avant de sauvegarder.</Step>
            </div>
            <Note>Si le document est de travers, utilisez les boutons de <strong>rotation</strong> (↺ ↻) qui apparaissent sous l'aperçu du scan pour relancer l'OCR dans la bonne orientation.</Note>
            <Tip>Les tickets de caisse bien éclairés et bien cadrés donnent les meilleurs résultats. Évitez les images floues ou trop sombres.</Tip>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="camera" className="border rounded-lg px-4">
          <AccordionTrigger className="text-sm font-medium py-3 gap-2">
            <span className="flex items-center gap-2"><Camera className="h-4 w-4" /> Prendre une photo depuis la caméra</span>
          </AccordionTrigger>
          <AccordionContent className="pb-3 space-y-2">
            <div className="space-y-1">
              <Step number="1">Cliquez sur <strong>"Prendre une photo"</strong> dans la zone d'upload.</Step>
              <Step number="2">Autorisez l'accès à la caméra si le navigateur le demande.</Step>
              <Step number="3">Cadrez votre justificatif, utilisez le bouton <strong>flip</strong> (🔄) pour basculer caméra avant/arrière.</Step>
              <Step number="4">Appuyez sur le bouton de capture. La photo est envoyée et l'OCR démarre.</Step>
            </div>
            <Warning>Sur iPhone, utilisez Safari pour accéder à la caméra. D'autres navigateurs mobiles peuvent ne pas supporter cette fonctionnalité.</Warning>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="kilometrage" className="border rounded-lg px-4">
          <AccordionTrigger className="text-sm font-medium py-3 gap-2">
            <span className="flex items-center gap-2"><Car className="h-4 w-4" /> Indemnités kilométriques</span>
          </AccordionTrigger>
          <AccordionContent className="pb-3 space-y-2">
            <p className="text-sm">Pour les frais de déplacement avec votre véhicule personnel, sélectionnez la catégorie <strong>"Kilométrage"</strong>.</p>
            <div className="space-y-1">
              <Step number="1">Le formulaire bascule en mode kilométrique.</Step>
              <Step number="2">Sélectionnez le <strong>type de véhicule</strong> (voiture, moto, scooter) et la <strong>puissance fiscale</strong> (CV).</Step>
              <Step number="3">Entrez le <strong>nombre de kilomètres</strong> parcourus.</Step>
              <Step number="4">Le montant se calcule automatiquement selon le <strong>barème DGFIP 2024</strong>.</Step>
            </div>
            <Tip>Configurez votre véhicule par défaut dans <strong>Mon profil</strong> pour ne pas avoir à le resélectionner à chaque fois.</Tip>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}

function SectionGererDepenses() {
  return (
    <div className="space-y-4">
      <SectionHeader icon={Receipt} title="Gérer mes dépenses" />

      <Accordion type="multiple" className="space-y-2">
        <AccordionItem value="liste" className="border rounded-lg px-4">
          <AccordionTrigger className="text-sm font-medium py-3 gap-2">
            <span className="flex items-center gap-2"><Filter className="h-4 w-4" /> Filtrer et rechercher</span>
          </AccordionTrigger>
          <AccordionContent className="pb-3 text-sm space-y-2">
            <p>La page <strong>Mes dépenses</strong> affiche toutes vos dépenses, groupées par année et par mois.</p>
            <ul className="space-y-1 list-disc list-inside text-muted-foreground">
              <li><strong>Recherche texte</strong> : par description, catégorie ou projet</li>
              <li><strong>Statut</strong> : brouillon, soumis, approuvé, rejeté, remboursé</li>
              <li><strong>Catégorie</strong> : filtrer par type de dépense</li>
              <li><strong>Période</strong> : date de début et de fin</li>
              <li><strong>Mois comptable</strong> : filtrer par mois de comptabilisation</li>
            </ul>
            <Tip>Cliquez sur une ligne d'année ou de mois pour déplier/replier le groupe correspondant.</Tip>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="actions" className="border rounded-lg px-4">
          <AccordionTrigger className="text-sm font-medium py-3">Actions sur une dépense</AccordionTrigger>
          <AccordionContent className="pb-3 text-sm space-y-2">
            <p>Pour chaque dépense, le menu d'actions (icône ⋯) propose selon le statut :</p>
            <ul className="space-y-1 list-disc list-inside text-muted-foreground">
              <li><strong>Voir</strong> : consulter le détail (tous statuts)</li>
              <li><strong>Modifier</strong> : éditer (brouillon ou rejeté uniquement)</li>
              <li><strong>Soumettre</strong> : envoyer pour validation (brouillon ou rejeté)</li>
              <li><strong>Remettre en brouillon</strong> : reprendre la main sur une dépense soumise</li>
              <li><strong>Supprimer</strong> : supprimer (brouillon ou rejeté uniquement)</li>
            </ul>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="bulk" className="border rounded-lg px-4">
          <AccordionTrigger className="text-sm font-medium py-3 gap-2">
            <span className="flex items-center gap-2"><CheckCheck className="h-4 w-4" /> Soumettre plusieurs dépenses à la fois</span>
          </AccordionTrigger>
          <AccordionContent className="pb-3 text-sm space-y-1">
            <Step number="1">Cochez les cases à gauche des dépenses souhaitées (statut brouillon ou rejeté).</Step>
            <Step number="2">Une barre flottante apparaît en bas de l'écran avec le total sélectionné.</Step>
            <Step number="3">Cliquez sur <strong>"Soumettre la sélection"</strong>.</Step>
            <Tip>Cochez la case de l'en-tête de mois pour tout sélectionner dans ce mois.</Tip>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="detail" className="border rounded-lg px-4">
          <AccordionTrigger className="text-sm font-medium py-3">Voir le détail d'une dépense</AccordionTrigger>
          <AccordionContent className="pb-3 text-sm space-y-2">
            <p>Cliquez sur une dépense pour ouvrir sa page de détail. Vous y trouverez :</p>
            <ul className="list-disc list-inside text-muted-foreground space-y-1">
              <li>Tous les champs saisis</li>
              <li>Les justificatifs en miniature (cliquez pour zoomer ou télécharger)</li>
              <li>L'historique des validations (qui a approuvé/rejeté et quand)</li>
              <li>La raison du rejet si applicable</li>
            </ul>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}

function SectionValidations() {
  return (
    <div className="space-y-4">
      <SectionHeader icon={CheckSquare} title="Valider les dépenses" roles={["Manager", "Comptable", "Admin"]} />

      <Accordion type="multiple" className="space-y-2">
        <AccordionItem value="approuver" className="border rounded-lg px-4">
          <AccordionTrigger className="text-sm font-medium py-3">Approuver ou rejeter</AccordionTrigger>
          <AccordionContent className="pb-3 text-sm space-y-1">
            <p>La page <strong>Validations</strong> présente deux onglets :</p>
            <ul className="list-disc list-inside text-muted-foreground space-y-1 mt-1">
              <li><strong>En attente</strong> : dépenses soumises par les collaborateurs</li>
              <li><strong>Approuvées</strong> : dépenses validées en attente de remboursement</li>
            </ul>
            <div className="space-y-1 mt-2">
              <Step number="1">Consultez le justificatif en cliquant sur la dépense.</Step>
              <Step number="2">Cliquez sur <strong>Approuver</strong> (✓ vert) pour valider, ou <strong>Rejeter</strong> (✗ rouge) pour refuser.</Step>
              <Step number="3">En cas de rejet, saisissez un motif qui sera communiqué au collaborateur.</Step>
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="rembourser" className="border rounded-lg px-4">
          <AccordionTrigger className="text-sm font-medium py-3">Marquer comme remboursé</AccordionTrigger>
          <AccordionContent className="pb-3 text-sm space-y-1">
            <p>Depuis l'onglet <strong>Approuvées</strong>, cliquez sur <strong>Rembourser</strong> après avoir effectué le virement. La dépense passe au statut <Badge className="bg-purple-500 text-white text-xs">Remboursé</Badge>.</p>
            <Warning>Cette action est irréversible. Assurez-vous que le virement a bien été effectué avant de confirmer.</Warning>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="bulk-appro" className="border rounded-lg px-4">
          <AccordionTrigger className="text-sm font-medium py-3 gap-2">
            <span className="flex items-center gap-2"><CheckCheck className="h-4 w-4" /> Actions groupées</span>
          </AccordionTrigger>
          <AccordionContent className="pb-3 text-sm space-y-1">
            <Step number="1">Cochez plusieurs dépenses dans la liste.</Step>
            <Step number="2">La barre de sélection propose : <strong>Approuver tout</strong>, <strong>Rejeter tout</strong> (avec un motif unique) ou <strong>Rembourser tout</strong>.</Step>
            <Tip>Filtrez par collaborateur avec le menu déroulant pour traiter les dépenses d'une personne en particulier.</Tip>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}

function SectionRapports() {
  return (
    <div className="space-y-4">
      <SectionHeader icon={BarChart3} title="Rapports & exports" roles={["Manager", "Comptable", "Admin"]} />

      <Accordion type="multiple" className="space-y-2">
        <AccordionItem value="tableaux" className="border rounded-lg px-4">
          <AccordionTrigger className="text-sm font-medium py-3">Tableaux de bord</AccordionTrigger>
          <AccordionContent className="pb-3 text-sm space-y-2">
            <p>La page <strong>Rapports</strong> affiche sur la période sélectionnée :</p>
            <ul className="list-disc list-inside text-muted-foreground space-y-1">
              <li>Répartition par statut (camembert)</li>
              <li>Répartition par catégorie (camembert)</li>
              <li>Tendance mensuelle (courbe)</li>
              <li>Totaux et compteurs par statut</li>
            </ul>
            <Tip>Utilisez le filtre <strong>Collaborateur</strong> pour voir les rapports d'un membre de l'équipe spécifique.</Tip>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="export-csv" className="border rounded-lg px-4">
          <AccordionTrigger className="text-sm font-medium py-3 gap-2">
            <span className="flex items-center gap-2"><Download className="h-4 w-4" /> Export CSV</span>
          </AccordionTrigger>
          <AccordionContent className="pb-3 text-sm space-y-1">
            <Step number="1">Sélectionnez la période souhaitée (par défaut : 6 derniers mois).</Step>
            <Step number="2">Cliquez sur <strong>"Exporter CSV"</strong>.</Step>
            <Step number="3">Le fichier contient toutes les colonnes : date, montant, devise, catégorie, statut, collaborateur, projet, TVA, notes.</Step>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="export-fec" className="border rounded-lg px-4">
          <AccordionTrigger className="text-sm font-medium py-3 gap-2">
            <span className="flex items-center gap-2"><Download className="h-4 w-4" /> Export FEC SAGE</span>
          </AccordionTrigger>
          <AccordionContent className="pb-3 text-sm space-y-2">
            <p>Génère un fichier au format <strong>Fichier des Écritures Comptables (FEC)</strong> compatible SAGE, prêt pour l'import en comptabilité.</p>
            <Note>Les paramètres comptables (codes journaux, comptes, codes TVA) sont configurables dans <strong>Paramètres → Entreprise</strong>. Assurez-vous qu'ils sont correctement renseignés avant d'exporter.</Note>
            <Step number="1">Vérifiez que les codes SAGE des collaborateurs sont assignés (Paramètres → Utilisateurs).</Step>
            <Step number="2">Sélectionnez la période, puis cliquez sur <strong>"Exporter FEC SAGE"</strong>.</Step>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}

function SectionParametres() {
  return (
    <div className="space-y-4">
      <SectionHeader icon={Settings} title="Paramètres" roles={["Comptable", "Admin"]} />

      <Accordion type="multiple" className="space-y-2">
        <AccordionItem value="entreprise" className="border rounded-lg px-4">
          <AccordionTrigger className="text-sm font-medium py-3 gap-2">
            <span className="flex items-center gap-2"><Building2 className="h-4 w-4" /> Entreprise</span>
          </AccordionTrigger>
          <AccordionContent className="pb-3 text-sm space-y-2">
            <p>Configurez les informations et règles de votre entreprise :</p>
            <ul className="list-disc list-inside text-muted-foreground space-y-1">
              <li><strong>Nom et logo</strong> : apparaissent dans la barre de navigation</li>
              <li><strong>Plafonds journaliers et mensuels</strong> : alertes si dépassés</li>
              <li><strong>Plafonds par catégorie</strong> : limite par type de dépense</li>
              <li><strong>Seuil d'approbation</strong> : montant à partir duquel une validation est requise</li>
              <li><strong>Paramètres SAGE</strong> : codes journaux, comptes généraux, comptes TVA pour l'export FEC</li>
            </ul>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="categories" className="border rounded-lg px-4">
          <AccordionTrigger className="text-sm font-medium py-3 gap-2">
            <span className="flex items-center gap-2"><Tag className="h-4 w-4" /> Catégories</span>
          </AccordionTrigger>
          <AccordionContent className="pb-3 text-sm space-y-2">
            <p>Créez et gérez les catégories disponibles pour les dépenses :</p>
            <ul className="list-disc list-inside text-muted-foreground space-y-1">
              <li>Nom, icône, couleur</li>
              <li>Montant maximum autorisé</li>
              <li>Justificatif obligatoire (oui/non)</li>
            </ul>
            <Warning>La suppression d'une catégorie est irréversible. Les dépenses existantes utilisant cette catégorie ne sont pas affectées.</Warning>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="utilisateurs" className="border rounded-lg px-4">
          <AccordionTrigger className="text-sm font-medium py-3 gap-2">
            <span className="flex items-center gap-2"><UserCog className="h-4 w-4" /> Utilisateurs</span>
          </AccordionTrigger>
          <AccordionContent className="pb-3 text-sm space-y-2">
            <p>Gérez les membres de votre organisation :</p>
            <ul className="list-disc list-inside text-muted-foreground space-y-1">
              <li><strong>Inviter</strong> un utilisateur par adresse e-mail</li>
              <li><strong>Changer le rôle</strong> d'un utilisateur (Admin uniquement)</li>
              <li><strong>Assigner un code SAGE</strong> pour les exports comptables</li>
              <li><strong>Supprimer</strong> un compte (Admin uniquement)</li>
            </ul>
            <Note>Seul un Administrateur peut modifier les rôles des autres utilisateurs.</Note>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}

function SectionProfil() {
  return (
    <div className="space-y-4">
      <SectionHeader icon={User} title="Mon profil" />

      <Accordion type="multiple" className="space-y-2">
        <AccordionItem value="vehicule" className="border rounded-lg px-4">
          <AccordionTrigger className="text-sm font-medium py-3">Configurer son véhicule</AccordionTrigger>
          <AccordionContent className="pb-3 text-sm space-y-1">
            <p>Dans <strong>Mon profil</strong> (menu en haut à droite), configurez votre véhicule par défaut pour les indemnités kilométriques :</p>
            <div className="space-y-1">
              <Step number="1">Sélectionnez le type de véhicule : voiture, moto ou scooter/cyclomoteur.</Step>
              <Step number="2">Choisissez la puissance fiscale (chevaux fiscaux).</Step>
              <Step number="3">Le taux kilométrique applicable (barème DGFIP 2024) s'affiche automatiquement.</Step>
              <Step number="4">Cliquez sur <strong>"Enregistrer"</strong>.</Step>
            </div>
            <Tip>Ces paramètres seront pré-remplis automatiquement lors de la saisie de frais kilométriques.</Tip>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}

// ─── PAGE PRINCIPALE ─────────────────────────────────────────────────────────

export default function Help() {
  const { hasRole } = useAuth();
  const isManagerOrAbove = hasRole("manager") || hasRole("accountant") || hasRole("admin");
  const isAccountantOrAbove = hasRole("accountant") || hasRole("admin");

  const tabs = [
    { value: "depart", label: "Démarrage", component: <SectionDepart /> },
    { value: "creer", label: "Créer une dépense", component: <SectionCreerDepense /> },
    { value: "gerer", label: "Mes dépenses", component: <SectionGererDepenses /> },
    ...(isManagerOrAbove ? [{ value: "validations", label: "Validations", component: <SectionValidations /> }] : []),
    ...(isManagerOrAbove ? [{ value: "rapports", label: "Rapports", component: <SectionRapports /> }] : []),
    ...(isAccountantOrAbove ? [{ value: "parametres", label: "Paramètres", component: <SectionParametres /> }] : []),
    { value: "profil", label: "Mon profil", component: <SectionProfil /> },
  ];

  const [activeTab, setActiveTab] = useState("depart");

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-lg bg-primary/10">
          <BookOpen className="h-6 w-6 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Mode d'emploi</h1>
          <p className="text-sm text-muted-foreground">Guide complet de l'application NoteFrais</p>
        </div>
      </div>

      {/* Contenu */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        {/* Tabs scrollables sur mobile */}
        <div className="overflow-x-auto pb-1 -mx-1 px-1">
          <TabsList className="flex w-max gap-1 h-auto p-1 flex-wrap md:flex-nowrap">
            {tabs.map(tab => (
              <TabsTrigger key={tab.value} value={tab.value} className="text-xs whitespace-nowrap">
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </div>

        {tabs.map(tab => (
          <TabsContent key={tab.value} value={tab.value}>
            <Card>
              <CardContent className="pt-6">
                {tab.component}
              </CardContent>
            </Card>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
